import React, { useEffect, useState } from 'react';
import { Users, Tag, BookOpen, Plus, Edit, Trash2, Save, X, Check, Settings, Bell, Building2, FolderOpen, MessageSquare, Clock, Shield, Upload, Package, ThumbsUp, Palette, RefreshCw, Play, Pause, History, Sparkles } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';

// ── Toggle switch component ───────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div style={{
      width: 40, height: 22, borderRadius: 11,
      background: value ? 'var(--accent)' : 'var(--border)',
      position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
    }} onClick={() => onChange(!value)}>
      <div style={{
        position: 'absolute', top: 3, left: value ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

// ── Simple inline editable list (for statuses / categories) ──
function SimpleListManager({ title, items, onAdd, onDelete, onEdit, addFields, colorPicker, extraFields }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({});
  const [adding, setAdding]   = useState(false);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleAdd = async () => {
    setAdding(true);
    try { await onAdd(form); setForm({}); setShowAdd(false); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setAdding(false); }
  };

  const handleEdit = async (id) => {
    try { await onEdit(id, editForm); setEditId(null); }
    catch (err) { toast.error(err.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await onDelete(id); }
    catch (err) { toast.error(err.message || 'Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 style={{ fontSize: 15 }}>{title}</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}><Plus size={13} /> Add</button>
      </div>

      {showAdd && (
        <div className="card card-sm mb-4">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {addFields.map(f => (
              <div key={f.key} className="form-group" style={{ marginBottom: 0, flex: f.flex || '1 1 160px' }}>
                <label className="form-label">{f.label}</label>
                {f.type === 'checkbox' ? (
                  <div style={{ height: 36, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                    <span className="text-sm text-muted">{f.checkLabel}</span>
                  </div>
                ) : f.type === 'color' ? (
                  <input type="color" value={form[f.key] || '#6b7280'} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ height: 36, width: 50, padding: 2, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }} />
                ) : (
                  <input className="form-input" type={f.type || 'text'} placeholder={f.placeholder}
                    value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
            <div className="flex gap-2" style={{ flexShrink: 0 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={adding}>Add</button>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {items.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No items yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                {addFields.filter(f => f.type !== 'checkbox' || f.showInTable).map(f => <th key={f.key}>{f.label}</th>)}
                {extraFields && extraFields.map(f => <th key={f.key}>{f.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {addFields.filter(f => f.type !== 'checkbox' || f.showInTable).map(f => (
                    <td key={f.key}>
                      {editId === item.id ? (
                        f.type === 'color' ? (
                          <input type="color" value={editForm[f.key] || item[f.key] || '#6b7280'}
                            onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                            style={{ height: 30, width: 44, padding: 2, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }} />
                        ) : f.type === 'checkbox' ? (
                          <input type="checkbox" checked={editForm[f.key] !== undefined ? editForm[f.key] : !!item[f.key]}
                            onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                        ) : (
                          <input className="form-input" value={editForm[f.key] !== undefined ? editForm[f.key] : (item[f.key] || '')}
                            onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                            style={{ padding: '4px 8px', fontSize: 13 }} />
                        )
                      ) : (
                        f.type === 'color' ? (
                          <div className="flex items-center gap-2">
                            <div style={{ width: 14, height: 14, borderRadius: '50%', background: item[f.key] }} />
                            <span className="text-muted text-sm">{item[f.key]}</span>
                          </div>
                        ) : f.render ? f.render(item) : (
                          <span>{item[f.key] || '—'}</span>
                        )
                      )}
                    </td>
                  ))}
                  {extraFields && extraFields.map(f => <td key={f.key}>{f.render(item)}</td>)}
                  <td>
                    {editId === item.id ? (
                      <div className="flex gap-1">
                        <button className="btn btn-primary btn-sm" onClick={() => handleEdit(item.id)}><Check size={12} /></button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}><X size={12} /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        {onEdit && <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(item.id); setEditForm({}); }}><Edit size={13} /></button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(item.id)} style={{ color: 'var(--red)' }}><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Organizations Tab ─────────────────────────────────────────
function OrgsTab() {
  const [orgs, setOrgs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({ name: '', domain: '', notes: '' });
  const [adding, setAdding]   = useState(false);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchOrgs();
    api.get('/users').then(d => setAllUsers(d.users || []));
  }, []);

  const fetchOrgs = () => {
    setLoading(true);
    api.get('/orgs').then(d => setOrgs(d.orgs || [])).finally(() => setLoading(false));
  };

  const selectOrg = (org) => {
    setSelected(org);
    api.get(`/orgs/${org.id}`).then(d => setMembers(d.members || []));
  };

  const addOrg = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setAdding(true);
    try {
      await api.post('/orgs', form);
      toast.success('Organization created');
      setForm({ name: '', domain: '', notes: '' });
      setShowAdd(false);
      fetchOrgs();
    } catch (err) { toast.error(err.message); }
    finally { setAdding(false); }
  };

  const saveEdit = async (id) => {
    try { await api.put(`/orgs/${id}`, editForm); toast.success('Saved'); setEditId(null); fetchOrgs(); }
    catch (err) { toast.error(err.message); }
  };

  const deleteOrg = async (id) => {
    if (!window.confirm('Delete this organization? Users and tickets will be unlinked.')) return;
    try { await api.delete(`/orgs/${id}`); toast.success('Deleted'); setSelected(null); fetchOrgs(); }
    catch (err) { toast.error(err.message); }
  };

  const toggleMember = async (userId, currentlyMember) => {
    await api.put(`/orgs/${selected.id}/members`, { user_id: userId, action: currentlyMember ? 'remove' : 'add' });
    api.get(`/orgs/${selected.id}`).then(d => setMembers(d.members || []));
  };

  const nonMembers = allUsers.filter(u => u.role === 'client' && !members.find(m => m.id === u.id));

  return (
    <div className="admin-orgs-grid" style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 320px' : '1fr', gap: 20 }}>
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 style={{ fontSize: 15 }}>Organizations</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}><Plus size={13} /> New Org</button>
        </div>

        {showAdd && (
          <div className="card card-sm mb-4">
            <div className="admin-grid-2">
              {[
                { k: 'name', l: 'Organization Name *', ph: 'Acme Corp' },
                { k: 'domain', l: 'Domain (optional)', ph: 'acme.com' },
              ].map(f => (
                <div key={f.k} className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{f.l}</label>
                  <input className="form-input" placeholder={f.ph} value={form[f.k]}
                    onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))} />
                </div>
              ))}
              <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                <label className="form-label">Notes</label>
                <input className="form-input" placeholder="Internal notes…" value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={addOrg} disabled={adding}>{adding ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          {loading ? <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
          : orgs.length === 0 ? <div className="empty-state"><Building2 size={32} /><h3>No organizations yet</h3></div>
          : (
            <table>
              <thead><tr><th>Name</th><th>Domain</th><th>Members</th><th>Tickets</th><th></th></tr></thead>
              <tbody>
                {orgs.map(o => (
                  <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => selectOrg(o)}>
                    <td>
                      {editId === o.id ? (
                        <input className="form-input" value={editForm.name ?? o.name}
                          onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                          onClick={e => e.stopPropagation()} style={{ padding: '4px 8px', fontSize: 13 }} />
                      ) : <span className="font-medium">{o.name}</span>}
                    </td>
                    <td className="text-muted text-sm">{o.domain || '—'}</td>
                    <td className="text-sm">{o.user_count}</td>
                    <td className="text-sm">{o.ticket_count}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {editId === o.id ? (
                        <div className="flex gap-1">
                          <button className="btn btn-primary btn-sm" onClick={() => saveEdit(o.id)}><Check size={12} /></button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(o.id); setEditForm({ name: o.name, domain: o.domain || '', notes: o.notes || '' }); }}><Edit size={13} /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => deleteOrg(o.id)} style={{ color: 'var(--red)' }}><Trash2 size={13} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Members panel */}
      {selected && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 style={{ fontSize: 15 }}>{selected.name} — Members</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}><X size={14} /></button>
          </div>
          <div className="card card-sm mb-3">
            <div className="text-sm font-medium" style={{ marginBottom: 8 }}>Current members</div>
            {members.length === 0 ? <p className="text-muted text-sm">No members yet.</p> : members.map(m => (
              <div key={m.id} className="flex justify-between items-center" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div className="font-medium text-sm">{m.full_name || m.email}</div>
                  <div className="text-muted text-sm">{m.email}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleMember(m.id, true)} style={{ color: 'var(--red)' }}><X size={13} /></button>
              </div>
            ))}
          </div>
          {nonMembers.length > 0 && (
            <div className="card card-sm">
              <div className="text-sm font-medium" style={{ marginBottom: 8 }}>Add client to org</div>
              {nonMembers.map(u => (
                <div key={u.id} className="flex justify-between items-center" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div className="font-medium text-sm">{u.full_name || u.email}</div>
                    <div className="text-muted text-sm">{u.email}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => toggleMember(u.id, false)}><Plus size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Categories Tab ────────────────────────────────────────────
function CategoriesTab() {
  const [ticketCats, setTicketCats] = useState([]);
  const [kbCats, setKbCats]         = useState([]);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = () => {
    api.get('/lookups/categories').then(d => setTicketCats(d.categories || []));
    api.get('/lookups/kb-categories').then(d => setKbCats(d.categories || []));
  };

  return (
    <div className="admin-grid-2" style={{ gap: 24 }}>
      <div>
        <SimpleListManager
          title="Ticket Categories"
          items={ticketCats}
          addFields={[{ key: 'name', label: 'Category Name', placeholder: 'e.g. Hardware' }]}
          onAdd={async (f) => {
            if (!f.name?.trim()) throw new Error('Name required');
            await api.post('/lookups/categories', f);
            api.get('/lookups/categories').then(d => setTicketCats(d.categories || []));
          }}
          onDelete={async (id) => {
            await api.delete(`/lookups/categories/${id}`);
            setTicketCats(c => c.filter(x => x.id !== id));
          }}
        />
      </div>
      <div>
        <SimpleListManager
          title="KB Categories"
          items={kbCats}
          addFields={[
            { key: 'name', label: 'Category Name', placeholder: 'e.g. Network' },
            { key: 'icon', label: 'Icon (emoji)', placeholder: '📁', flex: '0 0 80px' },
          ]}
          onAdd={async (f) => {
            if (!f.name?.trim()) throw new Error('Name required');
            await api.post('/lookups/kb-categories', f);
            api.get('/lookups/kb-categories').then(d => setKbCats(d.categories || []));
          }}
          onEdit={async (id, f) => {
            await api.put(`/lookups/kb-categories/${id}`, f);
            api.get('/lookups/kb-categories').then(d => setKbCats(d.categories || []));
          }}
          onDelete={async (id) => {
            await api.delete(`/lookups/kb-categories/${id}`);
            setKbCats(c => c.filter(x => x.id !== id));
          }}
        />
      </div>
    </div>
  );
}

// ── Statuses Tab ──────────────────────────────────────────────
function StatusesTab() {
  const [statuses, setStatuses] = useState([]);
  useEffect(() => { fetch(); }, []);
  const fetch = () => api.get('/lookups/statuses').then(d => setStatuses(d.statuses || []));

  return (
    <SimpleListManager
      title="Ticket Statuses"
      items={statuses}
      addFields={[
        { key: 'name',      label: 'Status Name', placeholder: 'e.g. Pending' },
        { key: 'color',     label: 'Color',        type: 'color' },
        { key: 'is_closed', label: 'Marks closed?', type: 'checkbox', checkLabel: 'Closed', showInTable: true },
      ]}
      onAdd={async (f) => {
        if (!f.name?.trim()) throw new Error('Name required');
        await api.post('/lookups/statuses', f);
        fetch();
      }}
      onEdit={async (id, f) => {
        await api.put(`/lookups/statuses/${id}`, f);
        fetch();
      }}
      onDelete={async (id) => {
        await api.delete(`/lookups/statuses/${id}`);
        setStatuses(s => s.filter(x => x.id !== id));
      }}
      extraFields={[{
        key: 'preview', label: 'Preview',
        render: item => <span className="badge" style={{ background: item.color + '18', color: item.color, border: `1px solid ${item.color}30` }}>{item.name}</span>
      }]}
    />
  );
}

// ── Users Tab ─────────────────────────────────────────────────
function UsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', full_name: '', password: '', role: 'client', company: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => { fetchUsers(); }, []);
  const fetchUsers = () => {
    setLoading(true);
    api.get('/users').then(d => setUsers(d.users || [])).finally(() => setLoading(false));
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error('Email and password required'); return; }
    setAdding(true);
    try {
      await api.post('/users', newUser);
      toast.success('User created');
      setShowAdd(false);
      setNewUser({ email: '', full_name: '', password: '', role: 'client', company: '' });
      fetchUsers();
    } catch (err) { toast.error(err.message); }
    finally { setAdding(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try { await api.delete(`/users/${id}`); toast.success('Deleted'); fetchUsers(); }
    catch (err) { toast.error(err.message); }
  };

  const roleColors = { admin: '#ef4444', tech: '#3b82f6', client: '#6b7280' };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 style={{ fontSize: 15 }}>Users</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}><Plus size={13} /> Add User</button>
      </div>
      {showAdd && (
        <div className="card card-sm mb-4">
          <div className="admin-grid-2">
            {[
              { label: 'Full name', key: 'full_name', type: 'text', ph: 'Jane Smith' },
              { label: 'Email *', key: 'email', type: 'email', ph: 'jane@example.com' },
              { label: 'Password *', key: 'password', type: 'password', ph: 'Min 6 chars' },
              { label: 'Company', key: 'company', type: 'text', ph: 'Acme Corp' },
            ].map(f => (
              <div key={f.key} className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{f.label}</label>
                <input className="form-input" type={f.type} placeholder={f.ph}
                  value={newUser[f.key]} onChange={e => setNewUser(u => ({ ...u, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Role</label>
              <select className="form-select" value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                <option value="client">Client</option>
                <option value="tech">Tech</option>
                <option value="admin">Admin</option>
                {currentUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
              </select>
            </div>
          </div>
          <div className="flex gap-2" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={createUser} disabled={adding}>{adding ? 'Creating…' : 'Create'}</button>
          </div>
        </div>
      )}
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
        : (
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Role</th><th>View Ratings</th><th>Delete Comments</th><th></th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="font-medium">{u.full_name || '—'}</td>
                  <td className="text-secondary text-sm">{u.email}</td>
                  <td className="text-muted text-sm">{u.company || '—'}</td>
                  <td>
                    {editId === u.id ? (
                      <select className="form-select" style={{ padding: '4px 8px', fontSize: 12 }} value={editRole} onChange={e => setEditRole(e.target.value)}>
                        <option value="client">Client</option><option value="tech">Tech</option><option value="admin">Admin</option>
                        {currentUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                      </select>
                    ) : (
                      <span className="badge" style={{ background: roleColors[u.role] + '18', color: roleColors[u.role], textTransform: 'capitalize' }}>{u.role}</span>
                    )}
                  </td>
                  <td>
                    {(u.role === 'tech' || u.role === 'admin') ? (
                      <button
                        className={`btn btn-sm ${u.can_view_ratings ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                        onClick={async () => {
                          await api.put(`/users/${u.id}`, { can_view_ratings: u.can_view_ratings ? 0 : 1 });
                          fetchUsers();
                        }}
                      >
                        {u.can_view_ratings ? '✓ Enabled' : 'Disabled'}
                      </button>
                    ) : <span className="text-muted text-sm">—</span>}
                  </td>
                  <td>
                    {(u.role === 'tech' || u.role === 'admin') ? (
                      <button
                        className={`btn btn-sm ${u.can_delete_comments ? 'btn-primary' : 'btn-ghost'}`}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                        onClick={async () => {
                          await api.put(`/users/${u.id}`, { can_delete_comments: u.can_delete_comments ? 0 : 1 });
                          fetchUsers();
                        }}
                      >
                        {u.can_delete_comments ? '✓ Enabled' : 'Disabled'}
                      </button>
                    ) : <span className="text-muted text-sm">—</span>}
                  </td>
                  <td>
                    {editId === u.id ? (
                      <div className="flex gap-1">
                        <button className="btn btn-primary btn-sm" onClick={async () => { await api.put(`/users/${u.id}`, { role: editRole }); toast.success('Updated'); setEditId(null); fetchUsers(); }}><Check size={12} /></button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}><X size={12} /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(u.id); setEditRole(u.role); }}><Edit size={13} /> Role</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => deleteUser(u.id)} style={{ color: 'var(--red)' }}><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── KB Tab ────────────────────────────────────────────────────
function KBTab() {
  const [articles, setArticles]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgs, setOrgs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState({ title: '', body: '', category_id: '', org_id: '', status: 'draft', tags: '' });
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    fetchArticles();
    api.get('/lookups/kb-categories').then(d => setCategories(d.categories || []));
    api.get('/orgs').then(d => setOrgs(d.orgs || []));
    // Auto-open new article editor if ?new=1 query param
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') {
      setEditing('new');
      setForm({ title: '', body: '', category_id: '', org_id: '', status: 'draft', tags: '' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchArticles = () => {
    setLoading(true);
    api.get('/kb/articles').then(d => setArticles(d.articles || [])).finally(() => setLoading(false));
  };

  const openNew  = () => { setEditing('new'); setForm({ title: '', body: '', category_id: '', org_id: '', status: 'draft', tags: '' }); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, body: a.body, category_id: a.category?.id || '', org_id: a.org_id || '', status: a.status, tags: (a.tags || []).join(', ') }); };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body required'); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing === 'new') await api.post('/kb/articles', payload);
      else await api.put(`/kb/articles/${editing.id}`, payload);
      toast.success('Saved'); setEditing(null); fetchArticles();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/kb/articles/${id}`); toast.success('Deleted'); fetchArticles(); }
    catch (err) { toast.error(err.message); }
  };

  const statusColors = { published: '#16a34a', draft: '#d97706', archived: '#6b7280' };

  if (editing !== null) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 style={{ fontSize: 15 }}>{editing === 'new' ? 'New Article' : 'Edit Article'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}><X size={14} /> Cancel</button>
        </div>
        <div className="card card-lg">
          <div className="form-group"><label className="form-label">Title</label><input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
          <div className="admin-grid-4">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                <option value="">Uncategorized</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <select className="form-select" value={form.org_id} onChange={e => setForm(f => ({ ...f, org_id: e.target.value }))}>
                <option value="">Public (all clients)</option>
                {orgs.map(o => <option key={o.id} value={o.id}>🔒 {o.name} only</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tags</label>
              <input className="form-input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="wifi, vpn" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Body (Markdown)</label>
            <textarea className="form-textarea" style={{ minHeight: 360, fontFamily: 'var(--font-mono)', fontSize: 13 }}
              value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} />
          </div>
          <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} />{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 style={{ fontSize: 15 }}>Knowledge Base Articles</h3>
        <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={13} /> New Article</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {loading ? <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
        : articles.length === 0 ? <div className="empty-state"><BookOpen size={32} /><h3>No articles yet</h3></div>
        : (
          <table>
            <thead><tr><th>Title</th><th>Category</th><th>Visibility</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {articles.map(a => (
                <tr key={a.id}>
                  <td className="font-medium">{a.title}</td>
                  <td className="text-sm text-secondary">{a.category?.name || '—'}</td>
                  <td className="text-sm">{a.org_id ? <span className="badge" style={{ background: 'var(--purple-light)', color: 'var(--purple)' }}>🔒 Private</span> : <span className="text-muted">Public</span>}</td>
                  <td><span className="badge" style={{ background: statusColors[a.status] + '18', color: statusColors[a.status], textTransform: 'capitalize' }}>{a.status}</span></td>
                  <td><div className="flex gap-1">
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)}><Edit size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => del(a.id)} style={{ color: 'var(--red)' }}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Settings Tab (imported from previous build, condensed) ────
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting]   = useState(false);

  useEffect(() => {
    api.get('/settings').then(d => setSettings(d.settings || {})).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/settings', settings); toast.success('Settings saved'); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    if (!testEmail) { toast.error('Enter a recipient email'); return; }
    setTesting(true);
    try { await api.post('/settings/test-email', { to: testEmail }); toast.success('Test email sent!'); }
    catch (err) { toast.error(`Failed: ${err.message}`); }
    finally { setTesting(false); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>;

  const Tog = ({ label, k, desc }) => (
    <div className="flex justify-between items-start" style={{ padding: '11px 0', borderBottom: '1px solid var(--border)' }}>
      <div><div style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</div>{desc && <div className="text-muted text-sm">{desc}</div>}</div>
      <div style={{ marginLeft: 16 }}><Toggle value={settings[k] === 'true'} onChange={v => set(k, v ? 'true' : 'false')} /></div>
    </div>
  );

  return (
    <div>
      {/* ── Branding ── */}
      <div className="card card-lg mb-4">
        <h3 style={{ fontSize: 14, marginBottom: 16 }}>Branding</h3>
        <div className="admin-grid-2" style={{ marginBottom: 16 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Site Name</label>
            <input className="form-input" value={settings.site_name || ''} onChange={e => set('site_name', e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Site URL</label>
            <input className="form-input" value={settings.site_url || ''} onChange={e => set('site_url', e.target.value)} />
          </div>
        </div>

        {/* Logo */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div className="form-label" style={{ marginBottom: 8 }}>Logo</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Preview */}
            {settings.logo_url && (
              <div style={{ flexShrink: 0 }}>
                <img
                  src={settings.logo_url.startsWith('/') ? `${window.location.origin}/api${settings.logo_url}` : settings.logo_url}
                  alt="Logo preview"
                  style={{ height: 48, maxWidth: 160, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6, padding: 4, background: 'var(--bg-secondary)' }}
                  onError={e => e.target.style.display = 'none'}
                />
              </div>
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label className="form-label">Logo URL</label>
                <input
                  className="form-input"
                  placeholder="https://example.com/logo.png"
                  value={settings.logo_url?.startsWith('/uploads/') ? '' : (settings.logo_url || '')}
                  onChange={e => set('logo_url', e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="text-muted text-sm">or upload a file:</span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ fontSize: 12 }}
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const form = new FormData();
                    form.append('logo', file);
                    try {
                      const token = localStorage.getItem('gt_token');
                      const resp = await fetch('/api/settings/logo-upload', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: form,
                      });
                      const data = await resp.json();
                      if (!resp.ok) throw new Error(data.error);
                      set('logo_url', data.url);
                      toast.success('Logo uploaded');
                    } catch (err) {
                      toast.error(err.message || 'Upload failed');
                    }
                    e.target.value = '';
                  }}
                />
                {settings.logo_url && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => set('logo_url', '')}>Remove</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Login Page Button ── */}
      <div className="card card-lg mb-4">
        <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: 14 }}>Login Page Button</h3>
          <Toggle value={settings.login_button_enabled === 'true'} onChange={v => set('login_button_enabled', v ? 'true' : 'false')} />
        </div>
        <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
          Show a customizable button on the login page (e.g. for downloading remote access software).
        </p>
        <div className="admin-grid-2" style={{ opacity: settings.login_button_enabled === 'true' ? 1 : 0.5 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Button Label</label>
            <input className="form-input" placeholder="Download Remote Access" value={settings.login_button_label || ''} onChange={e => set('login_button_label', e.target.value)} disabled={settings.login_button_enabled !== 'true'} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Button URL</label>
            <input className="form-input" placeholder="https://example.com/download" value={settings.login_button_url || ''} onChange={e => set('login_button_url', e.target.value)} disabled={settings.login_button_enabled !== 'true'} />
          </div>
        </div>
      </div>
      <div className="card card-lg mb-4">
        <h3 style={{ fontSize: 14, marginBottom: 8 }}>Email</h3>
        <Tog label="Use server built-in mailer" k="email_use_sendmail" desc="Uses host sendmail. Disable to use SMTP." />
        <div className="admin-grid-2" style={{ marginTop: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">From Name</label><input className="form-input" value={settings.email_from_name || ''} onChange={e => set('email_from_name', e.target.value)} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">From Address</label><input className="form-input" value={settings.email_from_address || ''} onChange={e => set('email_from_address', e.target.value)} /></div>
        </div>
        {settings.email_use_sendmail !== 'true' && (
          <div style={{ marginTop: 12, padding: 14, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <div className="admin-grid-2">
              {[
                { k: 'email_smtp_host', l: 'SMTP Host', ph: 'smtp.smtp2go.com' },
                { k: 'email_smtp_port', l: 'Port', ph: '587' },
                { k: 'email_smtp_user', l: 'Username', ph: 'you@example.com' },
                { k: 'email_smtp_password', l: 'Password', type: 'password', ph: '••••••••' },
              ].map(f => (
                <div key={f.k} className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{f.l}</label>
                  <input className="form-input" type={f.type || 'text'} placeholder={f.ph} value={settings[f.k] || ''} onChange={e => set(f.k, e.target.value)} />
                </div>
              ))}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Encryption</label>
                <select className="form-select" value={settings.email_smtp_encryption || 'tls'} onChange={e => set('email_smtp_encryption', e.target.value)}>
                  <option value="tls">TLS</option><option value="ssl">SSL</option><option value="none">None</option>
                </select>
              </div>
            </div>
          </div>
        )}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="form-group flex-1" style={{ marginBottom: 0 }}>
            <label className="form-label">Test recipient</label>
            <input className="form-input" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <button className="btn btn-secondary" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send Test'}</button>
        </div>
      </div>
      <div className="card card-lg mb-4">
        <h3 style={{ fontSize: 14, marginBottom: 4 }}>Notification Defaults</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 8 }}>Individual tickets can override these.</p>
        <Tog label="Confirm ticket receipt to client"    k="notify_client_ticket_created" />
        <Tog label="Notify staff of new tickets"         k="notify_staff_ticket_created" />
        <Tog label="Notify client when staff replies"    k="notify_client_ticket_reply" />
        <Tog label="Notify staff when client replies"    k="notify_staff_ticket_reply" />
        <Tog label="Notify tech when assigned"           k="notify_tech_ticket_assigned" />
        <Tog label="Notify client on status change"      k="notify_client_status_changed" />
      </div>
      {/* Time Tracking */}
      <div className="card card-lg mb-4">
        <div className="settings-section" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          <h3 className="settings-section-title"><Clock size={15} /> Time Tracking</h3>
          <div className="settings-row">
            <div><div className="settings-label">Time Rounding</div><div className="settings-hint">Logged time will be rounded up to the nearest interval</div></div>
            <select className="form-select" style={{ width: 140, marginLeft: 16 }} value={settings.time_rounding_minutes || '0'} onChange={e => set('time_rounding_minutes', e.target.value)}>
              <option value="0">None</option>
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Auto-Resolve */}
      <div className="card card-lg mb-4">
        <div className="settings-section" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          <h3 className="settings-section-title"><Clock size={15} /> Auto-Resolve</h3>
          <div className="settings-row">
            <div><div className="settings-label">Enable Auto-Resolve</div><div className="settings-hint">Automatically resolve tickets after a period of inactivity</div></div>
            <div style={{ marginLeft: 16 }}><Toggle value={settings.auto_resolve_enabled === 'true'} onChange={v => set('auto_resolve_enabled', v ? 'true' : 'false')} /></div>
          </div>
          <div style={{ opacity: settings.auto_resolve_enabled === 'true' ? 1 : 0.5, pointerEvents: settings.auto_resolve_enabled === 'true' ? 'auto' : 'none' }}>
            <div className="settings-row">
              <div><div className="settings-label">Inactivity Timeout</div><div className="settings-hint">Resolve ticket after this many days with no updates</div></div>
              <input className="form-input" type="number" min="1" style={{ width: 80, marginLeft: 16 }} value={settings.auto_resolve_days || '7'} onChange={e => set('auto_resolve_days', e.target.value)} />
            </div>
            <div className="settings-row">
              <div><div className="settings-label">Warning Email (days before)</div><div className="settings-hint">Send client a warning this many days before auto-resolve. Set to 0 to disable.</div></div>
              <input className="form-input" type="number" min="0" style={{ width: 80, marginLeft: 16 }} value={settings.auto_resolve_warning_days || '3'} onChange={e => set('auto_resolve_warning_days', e.target.value)} />
            </div>
            <div className="settings-row" style={{ alignItems: 'flex-start' }}>
              <div><div className="settings-label">Excluded Statuses</div><div className="settings-hint">Tickets with these statuses will never be auto-resolved</div></div>
              <div style={{ marginLeft: 16, minWidth: 200 }}>
                <AutoResolveStatusPicker value={settings.auto_resolve_excluded_statuses || ''} onChange={v => set('auto_resolve_excluded_statuses', v)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Satisfaction Ratings */}
      <div className="card card-lg mb-4">
        <div className="settings-section" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          <h3 className="settings-section-title"><ThumbsUp size={15} /> Satisfaction Ratings</h3>
          <div className="settings-row">
            <div><div className="settings-label">Enable Satisfaction Ratings</div><div className="settings-hint">Send clients a thumbs up/down prompt after their ticket is resolved</div></div>
            <div style={{ marginLeft: 16 }}><Toggle value={settings.rating_prompt_enabled === 'true'} onChange={v => set('rating_prompt_enabled', v ? 'true' : 'false')} /></div>
          </div>
          <div style={{ opacity: settings.rating_prompt_enabled === 'true' ? 1 : 0.5, pointerEvents: settings.rating_prompt_enabled === 'true' ? 'auto' : 'none' }}>
            <div className="settings-row">
              <div><div className="settings-label">Prompt Delay (days)</div><div className="settings-hint">Send the rating prompt this many days after the ticket is resolved</div></div>
              <input className="form-input" type="number" min="0" style={{ width: 80, marginLeft: 16 }} value={settings.rating_prompt_days || '1'} onChange={e => set('rating_prompt_days', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* AI & Automation */}
      <div className="card card-lg mb-4">
        <div className="settings-section" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          <h3 className="settings-section-title"><Sparkles size={15} /> AI & Automation</h3>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Anthropic API Key</label>
            <input className="form-input" type="password" placeholder="sk-ant-api03-…"
              value={settings.ai_api_key || ''} onChange={e => set('ai_api_key', e.target.value)} />
            <div className="text-muted text-sm" style={{ marginTop: 4 }}>Required for AI features. Get one at console.anthropic.com</div>
          </div>
          <div style={{ opacity: settings.ai_api_key && settings.ai_api_key !== '••••••••' ? 1 : 0.5 }}>
            <Tog label="Auto-categorize new tickets" k="ai_auto_categorize" desc="Use AI to suggest a category when a ticket is created" />
            <Tog label="Ticket thread summaries" k="ai_summaries_enabled" desc="Generate a brief AI summary at the top of ticket threads" />
          </div>
        </div>
      </div>

      {/* Sticky save bar */}
      <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg)', borderTop: '1px solid var(--border)', padding: '12px 0', marginTop: 8 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} />{saving ? 'Saving…' : 'Save Settings'}</button>
      </div>
    </div>
  );
}

// ── Auto-Resolve Status Picker ────────────────────────────────
function AutoResolveStatusPicker({ value, onChange }) {
  const [statuses, setStatuses] = useState([]);
  useEffect(() => { api.get('/lookups/statuses').then(d => setStatuses(d.statuses || [])).catch(console.error); }, []);
  const selected = value ? value.split(',').filter(Boolean) : [];
  function toggle(id) {
    const next = selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id];
    onChange(next.join(','));
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {statuses.map(s => (
        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
          {s.name}
        </label>
      ))}
    </div>
  );
}

// ── Canned Responses Tab ──────────────────────────────────────
function CannedTab() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null); // { id, title, body, sort_order } or 'new'
  const [form, setForm]           = useState({ title: '', body: '', sort_order: 0 });
  const [saving, setSaving]       = useState(false);

  useEffect(() => { load(); }, []);

  function load() {
    setLoading(true);
    api.get('/canned').then(d => setResponses(d.responses || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }

  function startNew() {
    setForm({ title: '', body: '', sort_order: (responses.length + 1) * 10 });
    setEditing('new');
  }

  function startEdit(r) {
    setForm({ title: r.title, body: r.body, sort_order: r.sort_order });
    setEditing(r.id);
  }

  async function save() {
    if (!form.title.trim() || !form.body.trim()) return toast.error('Title and body required');
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/canned', form);
      } else {
        await api.put(`/canned/${editing}`, form);
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this canned response?')) return;
    try { await api.delete(`/canned/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Canned Responses ({responses.length})</h3>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> New Response</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editing === 'new' ? 'New Response' : 'Edit Response'}</h4>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Ticket Received" />
          </div>
          <div className="form-group">
            <label className="form-label">Body</label>
            <textarea className="form-textarea" rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Response text..." />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Sort Order</label>
            <input className="form-input" type="number" style={{ width: 80 }} value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {responses.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No canned responses yet.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Title</th><th>Preview</th><th></th></tr>
            </thead>
            <tbody>
              {responses.map(r => (
                <tr key={r.id}>
                  <td className="font-medium">{r.title}</td>
                  <td className="text-muted text-sm" style={{ maxWidth: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.body}</td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(r)}><Edit size={12} /></button>
                      <button className="btn btn-ghost btn-sm" onClick={() => remove(r.id)} style={{ color: 'var(--red)' }}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── SLA Tab ───────────────────────────────────────────────────
function SlaTab() {
  const [rules, setRules]         = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ priority_id: '', org_id: '', response_hours: 24, resolution_hours: 72, is_24_7: true, enabled: true });
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/sla'),
      api.get('/lookups/priorities'),
      api.get('/orgs'),
    ]).then(([slaData, lookupData, orgData]) => {
      setRules(slaData.rules || []);
      setPriorities(lookupData.priorities || []);
      setOrgs(orgData.orgs || []);
    }).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  function load() {
    api.get('/sla').then(d => setRules(d.rules || [])).catch(e => toast.error(e.message));
  }

  function startNew() {
    setForm({ priority_id: priorities[0]?.id || '', org_id: '', response_hours: 24, resolution_hours: 72, is_24_7: true, enabled: true });
    setEditing('new');
  }

  function startEdit(r) {
    setForm({ priority_id: r.priority_id, org_id: r.org_id || '', response_hours: r.response_hours, resolution_hours: r.resolution_hours, is_24_7: !!r.is_24_7, enabled: !!r.enabled });
    setEditing(r.id);
  }

  async function save() {
    if (!form.priority_id) return toast.error('Priority required');
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/sla', form);
      else await api.put(`/sla/${editing}`, form);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this SLA rule?')) return;
    try { await api.delete(`/sla/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>;

  // Group rules: defaults first, then org overrides
  const defaults  = rules.filter(r => !r.org_id);
  const overrides = rules.filter(r =>  r.org_id);

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>SLA Rules</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Organization rules override default rules for that priority.</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> New Rule</button>
      </div>

      {editing && (
        <div className="card card-sm mb-4">
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editing === 'new' ? 'New SLA Rule' : 'Edit SLA Rule'}</h4>
          <div className="admin-grid-2">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority_id} onChange={e => setForm(f => ({ ...f, priority_id: e.target.value }))} disabled={editing !== 'new'}>
                <option value="">Select priority...</option>
                {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Organization (optional)</label>
              <select className="form-select" value={form.org_id} onChange={e => setForm(f => ({ ...f, org_id: e.target.value }))} disabled={editing !== 'new'}>
                <option value="">Default (all orgs)</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Response Time (hours)</label>
              <input className="form-input" type="number" min="0.5" step="0.5" value={form.response_hours} onChange={e => setForm(f => ({ ...f, response_hours: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Resolution Time (hours)</label>
              <input className="form-input" type="number" min="1" step="0.5" value={form.resolution_hours} onChange={e => setForm(f => ({ ...f, resolution_hours: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-4" style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_24_7} onChange={e => setForm(f => ({ ...f, is_24_7: e.target.checked }))} />
              24/7 (clock runs all the time)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
              Enabled
            </label>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {[{ label: 'Default Rules', rows: defaults }, { label: 'Organization Overrides', rows: overrides }].map(group => (
        group.rows.length > 0 && (
          <div key={group.label} style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 8px' }}>{group.label}</h4>
            <div className="card" style={{ padding: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Priority</th>
                    {group.rows.some(r => r.org_id) && <th>Organization</th>}
                    <th>Response</th>
                    <th>Resolution</th>
                    <th>24/7</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map(r => (
                    <tr key={r.id}>
                      <td>
                        <span className="flex items-center gap-2">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.priority_color, display: 'inline-block', flexShrink: 0 }} />
                          {r.priority_name}
                        </span>
                      </td>
                      {group.rows.some(row => row.org_id) && <td className="text-sm">{r.org_name || '—'}</td>}
                      <td className="text-sm">{r.response_hours}h</td>
                      <td className="text-sm">{r.resolution_hours}h</td>
                      <td className="text-sm">{r.is_24_7 ? '✓' : '—'}</td>
                      <td>
                        <span style={{ color: r.enabled ? '#10b981' : 'var(--text-muted)', fontWeight: 500, fontSize: 13 }}>{r.enabled ? 'Active' : 'Disabled'}</span>
                      </td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(r)}><Edit size={12} /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => remove(r.id)} style={{ color: 'var(--red)' }}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}
      {rules.length === 0 && !editing && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
          No SLA rules configured. Click "New Rule" to get started.
        </div>
      )}
    </div>
  );
}

// ── Import Tab ────────────────────────────────────────────────
function ImportTab() {
  const [csv, setCsv]           = useState('');
  const [preview, setPreview]   = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult]     = useState(null);
  const fileRef = React.useRef(null);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setCsv(ev.target.result); setPreview(null); setResult(null); };
    reader.readAsText(file);
  }

  async function doPreview() {
    if (!csv.trim()) return toast.error('Please paste or upload a CSV first');
    setPreviewing(true);
    try {
      const data = await api.post('/import/preview', { csv });
      setPreview(data);
    } catch (e) { toast.error(e.message); }
    finally { setPreviewing(false); }
  }

  async function doImport() {
    if (!csv.trim()) return toast.error('No CSV data');
    if (!window.confirm(`Import ${preview?.valid || '?'} tickets? This cannot be undone.`)) return;
    setImporting(true);
    try {
      const data = await api.post('/import/run', { csv });
      setResult(data);
      setPreview(null);
      setCsv('');
      toast.success(`Imported ${data.imported} tickets`);
    } catch (e) { toast.error(e.message); }
    finally { setImporting(false); }
  }

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>Import Tickets from CSV</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Import tickets from another help desk system.</p>
        </div>
        <a href="/api/import/sample" className="btn btn-secondary btn-sm" download><Upload size={13} /> Download Sample CSV</a>
      </div>

      {/* CSV Template reference */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 12 }}>
        <strong style={{ fontSize: 13 }}>Required columns:</strong> subject, description<br />
        <strong style={{ fontSize: 13 }}>Optional columns:</strong> status, priority, category, created_by_email, assigned_to_email, org_name, created_at<br />
        <span style={{ color: 'var(--text-muted)' }}>Status/priority/category values must match your existing names exactly. Unknown values fall back to defaults.</span>
      </div>

      {/* File upload */}
      <div style={{ marginBottom: 12 }}>
        <input type="file" accept=".csv" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} />
        <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current.click()}><Upload size={13} /> Choose CSV File</button>
        {csv && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-muted)' }}>{csv.split('\n').length - 1} rows loaded</span>}
      </div>

      {/* Or paste */}
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Or paste CSV directly</label>
        <textarea className="form-textarea" rows={6} value={csv} onChange={e => { setCsv(e.target.value); setPreview(null); setResult(null); }} placeholder="subject,description,status,priority..." style={{ fontFamily: 'monospace', fontSize: 12 }} />
      </div>

      <div className="flex gap-2" style={{ marginBottom: 20 }}>
        <button className="btn btn-secondary" onClick={doPreview} disabled={previewing || !csv.trim()}>{previewing ? 'Previewing…' : 'Preview'}</button>
        {preview && <button className="btn btn-primary" onClick={doImport} disabled={importing || preview.valid === 0}>{importing ? 'Importing…' : `Import ${preview.valid} Tickets`}</button>}
      </div>

      {/* Preview table */}
      {preview && (
        <div>
          <div style={{ marginBottom: 10, fontSize: 13 }}>
            <span style={{ color: '#10b981', fontWeight: 600 }}>{preview.valid} valid</span>
            {preview.total - preview.valid > 0 && <span style={{ color: '#ef4444', fontWeight: 600, marginLeft: 12 }}>{preview.total - preview.valid} will be skipped</span>}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Row</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Subject</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Priority</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Created By</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600 }}>Issues</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map(r => (
                  <tr key={r.row} style={{ borderBottom: '1px solid var(--border)', background: r.valid ? undefined : '#fff5f5' }}>
                    <td style={{ padding: '6px 10px' }}>{r.row}</td>
                    <td style={{ padding: '6px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subject || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{r.priority || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{r.status || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{r.created_by_email || '—'}</td>
                    <td style={{ padding: '6px 10px', color: '#ef4444' }}>{r.errors.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import result */}
      {result && (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: 14, fontSize: 13 }}>
          <strong>Import complete!</strong> {result.imported} tickets imported, {result.skipped} skipped.
          {result.errors.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 16, color: '#ef4444' }}>
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Asset Types Tab ───────────────────────────────────────────
function AssetTypesTab() {
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ name: '', color: '#6b7280', icon: 'box', custom_fields: [] });
  const [saving, setSaving]   = useState(false);
  const [newField, setNewField] = useState({ name: '', type: 'text', required: false, placeholder: '' });

  useEffect(() => { load(); }, []);

  function load() {
    api.get('/assets/types').then(d => setTypes(d.types || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }

  function startNew() {
    setForm({ name: '', color: '#6b7280', icon: 'box', custom_fields: [] });
    setEditing('new');
  }

  function startEdit(t) {
    let fields = [];
    try { fields = typeof t.custom_fields === 'string' ? JSON.parse(t.custom_fields) : (t.custom_fields || []); } catch {}
    setForm({ name: t.name, color: t.color, icon: t.icon, custom_fields: fields });
    setEditing(t.id);
  }

  async function save() {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/assets/types', form);
      else await api.put(`/assets/types/${editing}`, form);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm('Delete this asset type?')) return;
    try { await api.delete(`/assets/types/${id}`); load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  }

  function addField() {
    if (!newField.name.trim()) return toast.error('Field name required');
    setForm(f => ({ ...f, custom_fields: [...f.custom_fields, { ...newField }] }));
    setNewField({ name: '', type: 'text', required: false, placeholder: '' });
  }

  function removeField(i) {
    setForm(f => ({ ...f, custom_fields: f.custom_fields.filter((_, idx) => idx !== i) }));
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>Asset Types</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Define types and custom fields for each category of asset.</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={startNew}><Plus size={13} /> New Type</button>
      </div>

      {editing && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>{editing === 'new' ? 'New Asset Type' : 'Edit Asset Type'}</h4>
          <div className="admin-grid-3" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Hardware" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: 40, height: 36, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input className="form-input" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ flex: 1 }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Icon</label>
              <select className="form-select" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}>
                <option value="monitor">Monitor (Hardware)</option>
                <option value="key">Key (Software)</option>
                <option value="network">Network</option>
                <option value="box">Box (Other)</option>
              </select>
            </div>
          </div>

          {/* Custom fields */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Custom Fields</div>
            {form.custom_fields.map((field, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border)' }}>
                <span style={{ flex: 1, fontSize: 13 }}>{field.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{field.type}</span>
                {field.required && <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>Required</span>}
                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 5px', color: 'var(--red)' }} onClick={() => removeField(i)}><X size={11} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ flex: 1, minWidth: 120 }} placeholder="Field name" value={newField.name} onChange={e => setNewField(f => ({ ...f, name: e.target.value }))} />
              <select className="form-select" style={{ width: 110 }} value={newField.type} onChange={e => setNewField(f => ({ ...f, type: e.target.value }))}>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="textarea">Textarea</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={newField.required} onChange={e => setNewField(f => ({ ...f, required: e.target.checked }))} /> Required
              </label>
              <button className="btn btn-secondary btn-sm" onClick={addField}><Plus size={12} /> Add Field</button>
            </div>
          </div>

          <div className="flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {types.map(t => {
          let fields = [];
          try { fields = typeof t.custom_fields === 'string' ? JSON.parse(t.custom_fields) : (t.custom_fields || []); } catch {}
          return (
            <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                {fields.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fields.length} custom field{fields.length !== 1 ? 's' : ''}: {fields.map(f => f.name).join(', ')}</div>}
              </div>
              <div className="flex gap-1">
                <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px' }} onClick={() => startEdit(t)}><Edit size={12} /></button>
                <button className="btn btn-ghost btn-sm" style={{ padding: '3px 6px', color: 'var(--red)' }} onClick={() => remove(t.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
        {types.length === 0 && !editing && <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>No asset types yet.</div>}
      </div>
    </div>
  );
}

// ── Recurring Tickets Tab ─────────────────────────────────────
function RecurringTab() {
  const [templates, setTemplates]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(null); // null | 'new' | template object
  const [form, setForm]             = useState({});
  const [saving, setSaving]         = useState(false);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [techs, setTechs]           = useState([]);
  const [orgs, setOrgs]             = useState([]);
  const [historyId, setHistoryId]   = useState(null);
  const [history, setHistory]       = useState([]);

  const fetchTemplates = () =>
    api.get('/recurring').then(d => setTemplates(d.templates || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));

  useEffect(() => {
    fetchTemplates();
    api.get('/lookups/categories').then(d => setCategories(d.categories || []));
    api.get('/lookups/priorities').then(d => setPriorities(d.priorities || []));
    api.get('/users/techs').then(d => setTechs(d.users || []));
    api.get('/orgs').then(d => setOrgs(d.orgs || []));
  }, []);

  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  const scheduleLabel = (t) => {
    const time = t.schedule_time || '09:00';
    switch (t.schedule_type) {
      case 'daily':   return `Daily at ${time}`;
      case 'weekly':  return `${DAYS[t.schedule_day ?? 1]}s at ${time}`;
      case 'monthly': return `${t.schedule_day ?? 1}${ordSuffix(t.schedule_day ?? 1)} of each month at ${time}`;
      case 'custom':  return `Every ${t.custom_interval_days || 7} day${(t.custom_interval_days || 7) !== 1 ? 's' : ''} at ${time}`;
      default: return t.schedule_type;
    }
  };

  const ordSuffix = (n) => {
    if (n >= 11 && n <= 13) return 'th';
    switch (n % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
  };

  const openNew = () => {
    setEditing('new');
    setForm({ name: '', subject: '', description: '', category_id: '', priority_id: '', assigned_to: '', org_id: '',
              schedule_type: 'weekly', schedule_day: 1, schedule_time: '09:00', custom_interval_days: 7, enabled: true });
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, subject: t.subject, description: t.description,
              category_id: t.category_id || '', priority_id: t.priority_id || '',
              assigned_to: t.assigned_to || '', org_id: t.org_id || '',
              schedule_type: t.schedule_type, schedule_day: t.schedule_day ?? 1,
              schedule_time: t.schedule_time || '09:00',
              custom_interval_days: t.custom_interval_days ?? 7,
              enabled: !!t.enabled });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        schedule_day: ['weekly','monthly'].includes(form.schedule_type) ? parseInt(form.schedule_day) : null,
        custom_interval_days: form.schedule_type === 'custom' ? parseInt(form.custom_interval_days) : null,
      };
      if (editing === 'new') {
        await api.post('/recurring', payload);
        toast.success('Recurring ticket created');
      } else {
        await api.put(`/recurring/${editing.id}`, payload);
        toast.success('Template updated');
      }
      setEditing(null);
      fetchTemplates();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recurring template?')) return;
    try { await api.delete(`/recurring/${id}`); fetchTemplates(); toast.success('Deleted'); }
    catch (err) { toast.error(err.message); }
  };

  const toggleEnabled = async (t) => {
    try {
      await api.put(`/recurring/${t.id}`, { enabled: !t.enabled });
      fetchTemplates();
    } catch (err) { toast.error(err.message); }
  };

  const showHistory = async (id) => {
    setHistoryId(id);
    try {
      const d = await api.get(`/recurring/${id}/history`);
      setHistory(d.tickets || []);
    } catch { setHistory([]); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div className="spinner" /></div>;

  // Edit / New form
  if (editing) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}><X size={14} /></button>
          <h3 style={{ fontSize: 15 }}>{editing === 'new' ? 'New Recurring Ticket' : 'Edit Template'}</h3>
        </div>
        <div className="card card-lg">
          <div className="form-group">
            <label className="form-label">Template Name</label>
            <input className="form-input" placeholder="e.g. Weekly Server Maintenance" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Ticket Subject</label>
            <input className="form-input" placeholder="Subject line for the created ticket" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" placeholder="Ticket description / instructions" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div className="admin-grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                <option value="">None</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Priority</label>
              <select className="form-select" value={form.priority_id} onChange={e => setForm(f => ({ ...f, priority_id: e.target.value }))}>
                <option value="">Default</option>
                {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Assign to</label>
              <select className="form-select" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}>
                <option value="">Unassigned</option>
                {techs.map(t => <option key={t.id} value={t.id}>{t.full_name || t.email}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Organization</label>
              <select className="form-select" value={form.org_id} onChange={e => setForm(f => ({ ...f, org_id: e.target.value }))}>
                <option value="">None</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          <hr className="divider" />

          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Schedule</h4>

          <div className="admin-grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Frequency</label>
              <select className="form-select" value={form.schedule_type} onChange={e => setForm(f => ({ ...f, schedule_type: e.target.value }))}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom Interval</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Time (UTC)</label>
              <input className="form-input" type="time" value={form.schedule_time} onChange={e => setForm(f => ({ ...f, schedule_time: e.target.value }))} />
            </div>
            {form.schedule_type === 'weekly' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Day of Week</label>
                <select className="form-select" value={form.schedule_day} onChange={e => setForm(f => ({ ...f, schedule_day: parseInt(e.target.value) }))}>
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {form.schedule_type === 'monthly' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Day of Month</label>
                <input className="form-input" type="number" min="1" max="28" value={form.schedule_day} onChange={e => setForm(f => ({ ...f, schedule_day: parseInt(e.target.value) }))} />
              </div>
            )}
            {form.schedule_type === 'custom' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Every N Days</label>
                <input className="form-input" type="number" min="1" value={form.custom_interval_days} onChange={e => setForm(f => ({ ...f, custom_interval_days: parseInt(e.target.value) }))} />
              </div>
            )}
          </div>

          <div className="flex justify-between items-center" style={{ marginTop: 12 }}>
            <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: 13.5 }}>
              <Toggle value={form.enabled} onChange={v => setForm(f => ({ ...f, enabled: v }))} />
              {form.enabled ? 'Enabled' : 'Disabled'}
            </label>
            <div className="flex gap-2">
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name?.trim() || !form.subject?.trim() || !form.description?.trim()}>
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // History modal
  if (historyId) {
    const tmpl = templates.find(t => t.id === historyId);
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <button className="btn btn-ghost btn-sm" onClick={() => setHistoryId(null)}><X size={14} /></button>
          <h3 style={{ fontSize: 15 }}>History: {tmpl?.name || 'Template'}</h3>
        </div>
        {history.length === 0 ? (
          <div className="card"><div className="empty-state" style={{ padding: 40 }}><History size={24} /><p>No tickets created yet</p></div></div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="table-container">
              <table>
                <thead><tr><th>#</th><th>Subject</th><th>Status</th><th>Created</th></tr></thead>
                <tbody>
                  {history.map(t => (
                    <tr key={t.id}>
                      <td><span className="ticket-num">#{t.ticket_number}</span></td>
                      <td className="font-medium">{t.subject}</td>
                      <td>{t.status_name && <span className="badge" style={{ background: t.status_color + '22', color: t.status_color }}>{t.status_name}</span>}</td>
                      <td className="text-muted text-sm">{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 style={{ fontSize: 15 }}>Recurring Tickets</h3>
        <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={13} /> New Template</button>
      </div>

      {templates.length === 0 ? (
        <div className="card">
          <div className="empty-state" style={{ padding: 48 }}>
            <RefreshCw size={28} />
            <h3>No recurring tickets</h3>
            <p>Create a template to automatically generate tickets on a schedule.</p>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={openNew}><Plus size={13} /> New Template</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.map(t => (
            <div key={t.id} className="card card-sm" style={{ padding: '14px 18px' }}>
              <div className="flex justify-between items-start">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                    <span className="font-medium" style={{ fontSize: 14 }}>{t.name}</span>
                    {t.enabled ? (
                      <span className="badge" style={{ background: 'var(--green-light)', color: 'var(--green)', fontSize: 10 }}>Active</span>
                    ) : (
                      <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', fontSize: 10 }}>Paused</span>
                    )}
                  </div>
                  <div className="text-sm text-secondary" style={{ marginBottom: 2 }}>{t.subject}</div>
                  <div className="text-sm text-muted flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
                    <span><RefreshCw size={11} style={{ display: 'inline', verticalAlign: -1, marginRight: 3 }} />{scheduleLabel(t)}</span>
                    {t.assignee_name && <span>→ {t.assignee_name}</span>}
                    {t.priority_name && <span style={{ color: t.priority_color }}>{t.priority_name}</span>}
                    {t.category_name && <span>{t.category_name}</span>}
                  </div>
                  {t.next_run_at && t.enabled && (
                    <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                      Next: {new Date(t.next_run_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1" style={{ flexShrink: 0, marginLeft: 12 }}>
                  <button className="btn btn-ghost btn-sm" title="History" style={{ padding: '3px 6px' }} onClick={() => showHistory(t.id)}><History size={13} /></button>
                  <button className="btn btn-ghost btn-sm" title={t.enabled ? 'Pause' : 'Enable'} style={{ padding: '3px 6px' }} onClick={() => toggleEnabled(t)}>
                    {t.enabled ? <Pause size={13} /> : <Play size={13} />}
                  </button>
                  <button className="btn btn-ghost btn-sm" title="Edit" style={{ padding: '3px 6px' }} onClick={() => openEdit(t)}><Edit size={13} /></button>
                  <button className="btn btn-ghost btn-sm" title="Delete" style={{ padding: '3px 6px', color: 'var(--red)' }} onClick={() => handleDelete(t.id)}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Admin Shell ───────────────────────────────────────────────
const TABS = [
  { id: 'users',       label: 'Users',             icon: Users },
  { id: 'orgs',        label: 'Organizations',     icon: Building2 },
  { id: 'statuses',    label: 'Statuses',          icon: Tag },
  { id: 'categories',  label: 'Categories',        icon: FolderOpen },
  { id: 'kb',          label: 'Knowledgebase',     icon: BookOpen },
  { id: 'canned',      label: 'Canned Responses',  icon: MessageSquare },
  { id: 'asset_types', label: 'Asset Types',       icon: Package },
  { id: 'recurring',   label: 'Recurring',          icon: RefreshCw },
  { id: 'sla',         label: 'SLA',               icon: Shield },
  { id: 'import',      label: 'Import',            icon: Upload },
  { id: 'settings',    label: 'Settings',          icon: Settings },
];

const SUPER_TABS = [
  { id: 'theme', label: 'Theme', icon: Palette },
];

export default function AdminPage() {
  const { tab: tabParam } = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const validTabs  = ['users','orgs','statuses','categories','kb','canned','asset_types','recurring','sla','import','settings','theme'];
  const [tab, setTab] = useState(validTabs.includes(tabParam) ? tabParam : 'users');

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) setTab(tabParam);
  }, [tabParam]);

  const handleTabClick = id => {
    setTab(id);
    navigate(`/admin/${id}`, { replace: true });
  };

  const allTabs = isSuperAdmin ? [...TABS, ...SUPER_TABS] : TABS;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header"><h2>Admin Panel</h2></div>
      <div className="page-body">
        <select className="tab-mobile-select form-select" value={tab} onChange={e => handleTabClick(e.target.value)}>
          {allTabs.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <div className="flex gap-1 mb-6 tab-bar" style={{ borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
          {allTabs.map(t => (
            <button key={t.id} className="btn btn-ghost" onClick={() => handleTabClick(t.id)} style={{
              borderRadius: '6px 6px 0 0', flexShrink: 0,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              fontWeight: tab === t.id ? 600 : 400, paddingBottom: 10,
            }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        {tab === 'users'       && <UsersTab />}
        {tab === 'orgs'        && <OrgsTab />}
        {tab === 'statuses'    && <StatusesTab />}
        {tab === 'categories'  && <CategoriesTab />}
        {tab === 'kb'          && <KBTab />}
        {tab === 'canned'      && <CannedTab />}
        {tab === 'asset_types' && <AssetTypesTab />}
        {tab === 'recurring'   && <RecurringTab />}
        {tab === 'sla'         && <SlaTab />}
        {tab === 'import'      && <ImportTab />}
        {tab === 'settings'    && <SettingsTab />}
        {tab === 'theme'       && isSuperAdmin && <ThemeTab />}
      </div>
    </div>
  );
}

// ── Theme Tab (super_admin only) ──────────────────────────────
function ThemeTab() {
  const { applyGlobalTheme } = useTheme();
  const [primary, setPrimary] = useState('#1a1a18');
  const [sidebar, setSidebar] = useState('#1a1a18');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.get('/settings/theme').then(d => {
      if (d.theme?.primary_color) setPrimary(d.theme.primary_color);
      if (d.theme?.sidebar_color) setSidebar(d.theme.sidebar_color);
    }).finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put('/settings', { theme_primary_color: primary, theme_sidebar_color: sidebar });
      applyGlobalTheme({ primary_color: primary, sidebar_color: sidebar });
      toast.success('Theme saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  function preview() {
    applyGlobalTheme({ primary_color: primary, sidebar_color: sidebar });
    toast.success('Preview applied — save to make permanent');
  }

  if (loading) return <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="card mb-4">
        <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Palette size={14} /> Brand Colors
        </h3>

        <div className="settings-row">
          <div>
            <div className="settings-label">Primary Color</div>
            <div className="settings-hint">Used for buttons, links, and active states</div>
          </div>
          <div className="color-input-wrapper">
            <div className="color-swatch" style={{ background: primary }}>
              <input type="color" value={primary} onChange={e => setPrimary(e.target.value)} />
            </div>
            <input
              className="form-input"
              style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
              value={primary}
              onChange={e => setPrimary(e.target.value)}
              placeholder="#1a1a18"
            />
          </div>
        </div>

        <div className="settings-row">
          <div>
            <div className="settings-label">Sidebar Color</div>
            <div className="settings-hint">Background color of the navigation sidebar</div>
          </div>
          <div className="color-input-wrapper">
            <div className="color-swatch" style={{ background: sidebar }}>
              <input type="color" value={sidebar} onChange={e => setSidebar(e.target.value)} />
            </div>
            <input
              className="form-input"
              style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
              value={sidebar}
              onChange={e => setSidebar(e.target.value)}
              placeholder="#1a1a18"
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={preview}>Preview</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save Theme'}
        </button>
      </div>
    </div>
  );
}
